import React, { useEffect, useState } from "react";
import axios from "../axiosConfig";

const API_BASE = "/api";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Stored timestamps are UTC. Activity always displays GMT−5 (fixed offset, no DST). */
function formatGmtMinus5(iso) {
  if (!iso) return "";
  const raw = String(iso).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [year, month, day] = raw.split("-").map(Number);
    if (!year || !month || !day) return raw;
    return `${MONTHS[month - 1]} ${day}, ${year}`;
  }
  let normalized = raw;
  if (!/(?:Z|[+-]\d{2}:\d{2})$/i.test(normalized)) {
    normalized += "Z";
  }
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return raw;
  const shifted = new Date(date.getTime() - 5 * 60 * 60 * 1000);
  const hours24 = shifted.getUTCHours();
  const hours12 = hours24 % 12 || 12;
  const minutes = String(shifted.getUTCMinutes()).padStart(2, "0");
  const ampm = hours24 >= 12 ? "PM" : "AM";
  return `${MONTHS[shifted.getUTCMonth()]} ${shifted.getUTCDate()}, ${shifted.getUTCFullYear()}, ${hours12}:${minutes} ${ampm} GMT-5`;
}

/** API returns { title, createdAt }; older clients used plain strings. */
function articleEntryTitle(entry) {
  if (entry && typeof entry === "object" && entry.title != null) return entry.title;
  return String(entry ?? "");
}

function articleEntryCreated(entry) {
  if (entry && typeof entry === "object" && entry.createdAt) return entry.createdAt;
  return null;
}

function articleEntryAuthor(entry) {
  if (entry && typeof entry === "object" && entry.author) return String(entry.author);
  return "";
}

export default function Activity() {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_BASE}/stats`)
      .then((res) => {
        if (!cancelled) setStats(res.data);
      })
      .catch((err) => {
        console.error("Failed to fetch stats:", err);
        if (!cancelled) setError("Could not load usage statistics.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="w-full">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white sm:text-3xl">
          Activity
        </h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Search and content usage for this wiki (manual collection).
        </p>
      </div>

      {error && (
        <div
          className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
          role="alert"
        >
          {error}
        </div>
      )}

      {!stats && !error && (
        <p className="text-sm text-slate-600 dark:text-slate-400">Loading statistics…</p>
      )}

      {stats && (
        <div className="space-y-8">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 md:grid-cols-4 md:gap-4">
            <StatCard label="Total articles" value={stats.totalArticles} />
            <StatCard label="New this week" value={stats.newArticlesThisWeek} />
            <StatCard label="Total searches" value={stats.totalSearches} />
            <StatCard label="Searches this week" value={stats.weeklySearchCount} />
          </div>

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            <section className="flex flex-col rounded-lg border border-slate-200 bg-white p-5 dark:border-slate-600 dark:bg-slate-900/80">
              <h2 className="mb-1 text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Last 10 articles (this week)
              </h2>
              <p className="mb-3 text-xs text-slate-500 dark:text-slate-500">
                Newest first, by latest chunk timestamp. Up to ten titles from the past seven days. Times are GMT-5.
              </p>
              {stats.recentArticles?.length > 0 ? (
                <ul className="max-h-80 list-none space-y-2 overflow-y-auto pr-1 text-sm text-slate-800 dark:text-slate-200">
                  {stats.recentArticles.map((entry, i) => {
                    const title = articleEntryTitle(entry);
                    const created = articleEntryCreated(entry);
                    const author = articleEntryAuthor(entry);
                    return (
                      <li
                        key={`${title}-${i}`}
                        className="rounded border border-slate-100 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800/50"
                      >
                        <span className="font-medium text-slate-900 dark:text-slate-100">{title}</span>
                        <span className="mt-1 block text-xs text-slate-500 dark:text-slate-400">
                          Author: {author || "—"}
                        </span>
                        {created && (
                          <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-500">
                            {formatGmtMinus5(created)}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  No articles dated in the last seven days, or timestamps are missing on chunks.
                </p>
              )}
            </section>

            <section className="flex flex-col rounded-lg border border-slate-200 bg-white p-5 dark:border-slate-600 dark:bg-slate-900/80">
              <h2 className="mb-1 text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Last 10 searches
              </h2>
              <p className="mb-3 text-xs text-slate-500 dark:text-slate-500">
                Most recent distinct questions (deduplicated). Same question repeated later appears once, at its latest time. Times are GMT-5.
              </p>
              {stats.recentSearches?.length > 0 ? (
                <ul className="max-h-80 space-y-2 overflow-y-auto pr-1 text-sm text-slate-800 dark:text-slate-200">
                  {stats.recentSearches.map((s, i) => (
                    <li
                      key={`${s.question}-${i}`}
                      className="rounded border border-slate-100 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800/50"
                    >
                      <span className="block text-slate-900 dark:text-slate-100">{s.question}</span>
                      <span className="mt-1 block text-xs text-slate-500 dark:text-slate-400">
                        Engineer: {s.askedBy || "—"}
                      </span>
                      {s.timestamp && (
                        <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-500">
                          {formatGmtMinus5(s.timestamp)}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  No searches logged yet. Questions appear here after Q&amp;A runs with logging enabled.
                </p>
              )}
            </section>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 dark:border-slate-600 dark:bg-slate-900/80">
      <div className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-slate-900 dark:text-white">
        {value ?? "—"}
      </div>
    </div>
  );
}
